import React, { FunctionComponent, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  DataSharingTableData,
  DistrictAccessDataSetting,
  InviteStatuses,
  School,
  SendInviteRequest,
  TeacherInfo,
} from "../../../../../../store/onboarding/types";
import BootstrapTable, { SortOrder, TableChangeState, TableChangeType } from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faEye, faTrash } from "@fortawesome/free-solid-svg-icons";
import LoadingIndicator from "../../../LoadingIndicator";
import DataSharingEditModal from "../../../../reading-interventions/educators-and-sharing/modals/DataSharingEditModal";
import InviteTeachersModal from "../../../../reading-interventions/educators-and-sharing/modals/InviteTeachersModal";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "../../../../../../store";
import {
  changeSelectedTeacher,
  changeStudentsFilter,
  deleteDataSharingTableData,
  getDataSharingTableData,
  getDistrictAccessDataSettings,
  getSchools,
  getTeachers,
  openDataSharingEditModal,
  openDistrictAccessDataSettingUpsertModal,
  openShareDataModal,
  openTeacherDetailsModal,
  setNewInviteData,
} from "../../../../../../store/onboarding/actions";
import { useLoading } from "../../../../../../utils/hooks/useLoading";
import { showConfirmDialog } from "../../../../../../store/confirm-dialog/actions";
import { getInviteStatusDisplay } from "../../../../../../utils/NamesUtils";
import DistrictAccessDataSettingUpsertModal from "../../../../../pages/district-owner-dashboard/tabs/data-access/DistrictAccessDataSettingUpsertModal";
import { UserInfo } from "../../../../../../store/auth/types";
import ResendInviteButton from "../../../../../pages/district-owner-dashboard/tabs/your-staff/manage-users-page/ResendInviteButton";
import _ from "lodash";

type OwnProps = {};

type Props = OwnProps;

type MergedData = {
  teacher?: TeacherInfo;
};

const MyTeachersTable: FunctionComponent<Props> = (props) => {
  const dispatch = useDispatch();
  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | undefined>(undefined);
  const isLoadingSendInviteToTeachers = useSelector<ApplicationState, boolean>(
    (s) => s.onboarding.isLoading.sendInviteToTeachers
  );
  const currentUser = useSelector<ApplicationState, UserInfo | undefined>(
      (s) => s.auth.userInfo
    );
  const loadingTeachersList = useSelector<
    ApplicationState,
    boolean | undefined
  >((s) => s.onboarding.isLoading.getTeachers);

  const loadingDataSharingList = useSelector<
    ApplicationState,
    boolean | undefined
  >((s) => s.onboarding.isLoading.getDataSharingTableData);

  const errorSendInviteToTeachers = useSelector<
    ApplicationState,
    string | undefined
  >((s) => s.onboarding.errors.sendInviteToTeachers);
  // useLoading(isLoadingSendInviteToTeachers, errorSendInviteToTeachers, () =>{
  //   dispatch(getDataSharingTableData())
  // }
  // ); 


  // const tableData = useSelector<ApplicationState, DataSharingTableData[]>(
  //   (s) => s.onboarding.dataSharingTableData
  // );
  const teachers = useSelector<ApplicationState, TeacherInfo[]>(
    (s) => s.onboarding.teachersRoster
  );

  const teachersFilter = useSelector<ApplicationState, string>(
    (s) => s.onboarding.teachersFilter
  );

  // Helper function to get schools string
  const getSchoolsString = (teacher: TeacherInfo): string => {
    if (teacher?.user.profile.current_assignment) {
      const schoolsNames: Array<String> = teacher.user.profile.current_assignment!.schools.map(
        (school) => school.name
      );
      if (schoolsNames) {
        return schoolsNames.join(", ");
      }
    }
    return "";
  };

  // Calculate match priority for intelligent sorting (lower number = higher priority)
  const getMatchPriority = (teacher: TeacherInfo, searchTerm: string): number => {
    if (!searchTerm) return 0;

    const query = searchTerm.trim();
    const queryLower = query.toLowerCase();

    // Check numeric fields FIRST (exact match only)
    const numClasses = teacher.info?.classes?.toString().trim() || '';
    const numStudents = teacher.info?.students?.toString().trim() || '';
    
    if (numClasses === query || numStudents === query) {
      return 1; // Exact numeric match
    }

    const firstName = teacher.user.first_name?.toLowerCase().trim() || '';
    const lastName = teacher.user.last_name?.toLowerCase().trim() || '';
    const fullName = `${firstName} ${lastName}`.trim();
    const reverseName = `${lastName} ${firstName}`.trim();
    const email = teacher.user.email?.toLowerCase().trim() || '';
    const emailDisplay = (email.indexOf("njtss_user_imported") >= 0 || email.indexOf("njtssuser") >= 0)
      ? 'imported from file'
      : email;
    const schoolsStr = getSchoolsString(teacher).toLowerCase();
    const inviteStatus = getInviteStatusDisplay(teacher).toLowerCase();

    // Priority 1: Exact matches
    if (firstName === queryLower || lastName === queryLower || fullName === queryLower || reverseName === queryLower) {
      return 1;
    }

    // Priority 2: Starts with first name or last name
    if (firstName.startsWith(queryLower) || lastName.startsWith(queryLower)) {
      return 2;
    }

    // Priority 3: Full name or reverse full name starts with query
    if (fullName.startsWith(queryLower) || reverseName.startsWith(queryLower)) {
      return 3;
    }

    // Priority 4: Email matches
    if (emailDisplay === queryLower) return 1;
    if (emailDisplay.startsWith(queryLower)) return 4;

    // Priority 5: School matches (with multi-word support)
    if (schoolsStr) {
      const schools = schoolsStr.split(',').map(s => s.trim());
      for (const school of schools) {
        if (school === queryLower) return 1;
        if (school.startsWith(queryLower)) return 5;
        
        // Word boundary match
        const schoolWords = school.split(/\s+/);
        for (const word of schoolWords) {
          if (word.startsWith(queryLower)) return 5;
        }
      }
    }

    // Priority 6: Invite Status matches
    if (inviteStatus === queryLower) return 1;
    if (inviteStatus.startsWith(queryLower)) return 6;

    return 999; // No match
  };

  // Optimized filter and sort using pre-built arrays (matches StudentsTable logic)
  const getFilteredAndSortedTeachers = (teachersList: TeacherInfo[], applySearchSort: boolean = true): TeacherInfo[] => {
    const searchTerm = teachersFilter.trim();
    
    if (!searchTerm) {
      return teachersList;
    }

    const query = searchTerm.trim();
    const queryLower = query.toLowerCase();
    
    // If column sorting is active, only filter, don't sort by search relevance
    if (!applySearchSort) {
      return teachersList.filter(teacher => {
        const priority = getMatchPriority(teacher, searchTerm);
        return priority < 999;
      });
    }
    
    // Build search arrays for efficient matching
    const firstNameMatches: TeacherInfo[] = [];
    const lastNameMatches: TeacherInfo[] = [];
    const otherMatches: TeacherInfo[] = [];

    for (const teacher of teachersList) {
      const firstName = teacher.user.first_name?.toLowerCase().trim() || '';
      const lastName = teacher.user.last_name?.toLowerCase().trim() || '';
      const numClasses = teacher.info?.classes?.toString().trim() || '';
      const numStudents = teacher.info?.students?.toString().trim() || '';

      // Priority Group 1: First name matches (exact or starts with) or Numeric ID
      if (
        firstName === queryLower ||
        firstName.startsWith(queryLower) ||
        numClasses === query ||
        numStudents === query
      ) {
        firstNameMatches.push(teacher);
        continue;
      }

      // Priority Group 2: Last name matches (exact or starts with)
      if (
        lastName === queryLower ||
        lastName.startsWith(queryLower)
      ) {
        lastNameMatches.push(teacher);
        continue;
      }

      // Priority Group 3: Full name or other field matches
      const priority = getMatchPriority(teacher, searchTerm);
      if (priority < 999) {
        otherMatches.push(teacher);
      }
    }

    // Sort within each group by exact match first, then alphabetically
    const sortGroup = (group: TeacherInfo[]) => {
      return group.sort((a, b) => {
        const aPriority = getMatchPriority(a, searchTerm);
        const bPriority = getMatchPriority(b, searchTerm);
        
        if (aPriority !== bPriority) {
          return aPriority - bPriority;
        }
        
        // If same priority, sort by name
        const aName = `${a.user.first_name} ${a.user.last_name}`.toLowerCase();
        const bName = `${b.user.first_name} ${b.user.last_name}`.toLowerCase();
        return aName.localeCompare(bName);
      });
    };

    // Combine results: FIRST NAME matches first, then last name, then others
    return [
      ...sortGroup(firstNameMatches),
      ...sortGroup(lastNameMatches),
      ...sortGroup(otherMatches)
    ];
  };
  
  // const firstUpdate = useRef(true);
  // useEffect(()=> {
  //   if(firstUpdate.current){
  //     dispatch(getDataSharingTableData())
  //     firstUpdate.current = false;
  //     return;
  //   }
  // },[teachers])

  const mergedData: TeacherInfo[] = useMemo(() => {
    let mergeTableData = teachers;
        
    if (mergeTableData.length) {
      // Apply search filter: if column sort is active, only filter (don't sort by search relevance)
      const hasColumnSort = sortField && sortOrder;
      mergeTableData = getFilteredAndSortedTeachers(mergeTableData, !hasColumnSort);
    } 
    return mergeTableData;     
  }, [teachers, teachersFilter, sortField, sortOrder]);

  const handleTableChange = (type: TableChangeType, { sortField: clickedField }: TableChangeState<any>) => {
    if (type === 'sort') {
      let newSortField: string | undefined = clickedField;
      let newSortOrder: 'asc' | 'desc' | undefined = 'asc';
  
      if (sortField === clickedField) {
        if (sortOrder === 'asc') {
          newSortOrder = 'desc';
        } else if (sortOrder === 'desc') {
          // Third click → Clear the sort
          newSortField = undefined;
          newSortOrder = undefined;
        }
      }
  
      setSortField(newSortField);
      setSortOrder(newSortOrder);
    }
  };

  const isEmpty = useMemo(() => {
    if(loadingDataSharingList || loadingTeachersList) {
      return <LoadingIndicator type="table" rows={20} columns={7} />
    } else if(mergedData.length === 0 && !loadingTeachersList) {
      return "No matching teachers"; 
    } 
  },[mergedData, loadingTeachersList, loadingDataSharingList])

  const columnEvents = {
    onClick: (
      e: React.MouseEvent,
      column: any,
      columnIndex: number,
      row: any
      //row: DataSharingTableData
    ) => {
      const selectedTeacher = teachers.find((data) => data.user.id === row.id);
      if (selectedTeacher) {
          dispatch(changeSelectedTeacher(selectedTeacher));
          dispatch(openTeacherDetailsModal());
        }
      // const selectedData = tableData.find((data) => data.id === row.id);
      // if (selectedData) {
      //   const selectedTeacher = teachers.find(
      //     (teacher) => teacher.user.id === selectedData.user.id
      //   );
      //   if (selectedTeacher) {
      //     dispatch(changeSelectedTeacher(selectedTeacher));
      //     dispatch(openTeacherDetailsModal());
      //   }
      // }
    },
  };

  const columns = [
    {
      dataField: "id",
      text: "ID",
      hidden: true,
    },
    {
      dataField: "date",
      text: "Date",
      hidden: true,
    },
    {
      dataField: "user",
      text: "User",
      sort: true,
      events: columnEvents,
      headerStyle: { width: "12%" }
    },
    {
      dataField: "email",
      text: "Email",
      sort: true,
      events: columnEvents,
      headerStyle: { width: "20%" },
      formatter: (cell: any, row: any) => row.emailDisplay 
    },
    {
      dataField: "school",
      text: "School(s)",
      sort: true,
      events: columnEvents,
      headerStyle: { width: "20%" }
    },
    {
      dataField: "numClasses",
      text: "# Class(es)",
      sort: true,
      events: columnEvents,
      headerStyle: { width: "11%" }
    },
    {
      dataField: "numStudents",
      text: "# Student(s)",
      sort: true,
      events: columnEvents,
      headerStyle: { width: "12%" }
    },
    {
      dataField: "inviteStatus",
      text: "Invite Status",
      sort: true,
      headerStyle: { width: "11%" },
    },
    {
      dataField: "actions",
      text: "Data Permissions",
      sort: false,
      headerStyle: { width: "13%"},
      headerAlign: 'left'
    },
  ];

  // const defaultSorted: [{ dataField: string; order: SortOrder }] = useMemo(
  //   () => [
  //     {
  //       dataField: "user",
  //       order: "asc",
  //     },
  //   ],
  //   []
  // );

  const options = useMemo(
    () => ({
      sizePerPageList: [
        {
          text: "20",
          value: 20,
        },
        {
          text: "50",
          value: 50,
        },
        {
          text: "100",
          value: 100,
        },
      ],
    }),
    []
  );

  useEffect(() => {
    dispatch(getSchools());
    if(!(teachers.length || loadingTeachersList)) {
      dispatch(getTeachers());
    }
    dispatch(getDistrictAccessDataSettings());
  }, []);

  const schools = useSelector<ApplicationState, School[]>(
    (s) => s.onboarding.schools
  );
  const districtAccessDataSettings = useSelector<
    ApplicationState,
    DistrictAccessDataSetting[]
  >((s) => s.onboarding.districtAccessDataSettings);

  const [selectedUser, setSelectedUser] = useState<
    DataSharingTableData | undefined
  >();

  // const handleEditRow = (selectedUser: DataSharingTableData) => {
  //   setSelectedUser(selectedUser);
  //   dispatch(openDataSharingEditModal());
  // };

  const handleView = (
    educator: UserInfo,
    setting?: DistrictAccessDataSetting
  ) => {
    dispatch(
      openDistrictAccessDataSettingUpsertModal(
        setting ? { ...(setting as any), user: educator } : { user: educator }
      )
    );
  };

  const handleDeleteRow = (row: DataSharingTableData) => {
    const onConfirm = () => {
      dispatch(deleteDataSharingTableData(row));
    };
    dispatch(showConfirmDialog({ onConfirm, centered: true }));
  };

  const handleInviteFromFile = (data: DataSharingTableData) => () => {
    const invite: SendInviteRequest = {
      first_name: data.user.first_name,
      last_name: data.user.last_name,
      email: "",
      existing_user_id: data.user.id,
      originalUser: data.user,
      teachers: [...data.teachers.map((t) => t.id), data.user.id],
      grades: data.grades,
    };
    dispatch(setNewInviteData(invite));
    dispatch(openShareDataModal('inviteFromFile'));
  };

  const getEmailDisplay = (email?: string) => {
    if (!email) {
      return null;
    }
    if (email.indexOf("njtss_user_imported") >= 0 || email.indexOf("njtssuser") >= 0) {
      return "Imported From File";
    } else {
      return <span>{email}</span>;
    }
  };

  const showSchools = (teacher: TeacherInfo) => {
    return getSchoolsString(teacher);
  };
  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
  const sortedMergedData = useMemo(() => {
    const originalData = _.cloneDeep(mergedData);
    const sortedData = [...originalData];
    sortedData.sort((a: TeacherInfo, b: TeacherInfo) => {
      let aValue: any;
      let bValue: any;
  
      switch (sortField) {
        case "user":
          aValue = `${a.user.first_name} ${a.user.last_name}`.trim();
          bValue = `${b.user.first_name} ${b.user.last_name}`.trim();
          break;
        case "email":
          aValue = a
                    ? ((a?.user.email.indexOf("njtss_user_imported") >= 0)
                      ||  (a?.user.email.indexOf("njtssuser") >= 0)
                    ? 'Imported From File' : a?.user.email)
                    : '';
          bValue = b 
                    ? ((b?.user.email.indexOf("njtss_user_imported") >= 0 )
                      ||  (b?.user.email.indexOf("njtssuser") >= 0)
                    ? 'Imported From File' : b?.user.email)
                    : '';
          break;
        case "school":
          aValue = showSchools(a!);
          bValue = showSchools(b!);
          break;
        case "inviteStatus":
          aValue = a 
            ? getInviteStatusDisplay(a) 
            : '';
          bValue = b 
            ? getInviteStatusDisplay(b) 
            : '';
          break;
        case "numClasses":
          aValue = a?.info.classes ?? 0;
          bValue = b?.info.classes ?? 0;
          break;
        case "numStudents":
          aValue = a?.info.students ?? 0;
          bValue = b?.info.students ?? 0;
          break;
        default:
          return 0;
      }
      const isEmpty = (val: any) => val === null || val === undefined || val === 'N/A';
        
      if (isEmpty(aValue) && !isEmpty(bValue)) return 1;
      if (!isEmpty(aValue) && isEmpty(bValue)) return -1;
      if (isEmpty(aValue) && isEmpty(bValue)) return 0;
      
      aValue = aValue.toString().trim();
      bValue = bValue.toString().trim();
  
      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortOrder === "asc"
          ? collator.compare(aValue, bValue)
          : collator.compare(bValue, aValue);
      }
      return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
    });
    return sortedData;
    
  }, [mergedData, sortField, sortOrder]);

  useLayoutEffect(() => {
    const classListDesc = (document.getElementsByClassName('caret-4-desc') as HTMLCollectionOf<HTMLElement>)?.[0];
    const classListAsc = (document.getElementsByClassName('caret-4-asc') as HTMLCollectionOf<HTMLElement>)?.[0];
    if (sortOrder === 'asc') { 
      if (classListAsc) classListAsc.style.display = 'inline-block';
      if (classListDesc) classListDesc.style.display = 'none';
    } 
    else if (sortOrder === 'desc') {
      if (classListDesc) classListDesc.style.display = 'inline-block';
      if (classListAsc) classListAsc.style.display = 'none';
    } 
    else if (!sortField && !sortOrder) {
      if (classListAsc) classListAsc.style.display = 'none';
      if (classListDesc) classListDesc.style.display = 'none';
    }
  }, [sortField, sortOrder]);

  
  return (
    <>
    {loadingTeachersList 
    ?
      <LoadingIndicator type="table" rows={20} columns={7} />
     :
      <div className={` ${sortedMergedData.length ? 'noOverflow' : ''}`}>
        <BootstrapTable
          remote={{ sort: true }}
          classes={`not-fixed rosterTable`}
          wrapperClasses={sortedMergedData.length === 0 ? "rosterTableEmpty" : ""}
          keyField="id"
          bordered={false}
          bootstrap4
          data={sortedMergedData
            .filter((rowData) => { //loggedin user not shown in the list
              let currentEmailAddress = currentUser?.email.replace('@','');
              return !rowData?.user?.email.includes(currentEmailAddress!)
            })
            .map((teacher) => { 
            const settings = districtAccessDataSettings.find(
              (s) => s.user === teacher?.user.id
            );
          return {
            id: teacher.user.id,
            date: '',
            user: `${teacher.user.first_name} ${teacher.user.last_name}`,
            email: teacher 
                    ? (((teacher.user.email.indexOf("njtss_user_imported") >= 0) 
                      || (teacher.user.email.indexOf("njtssuser") >= 0)) 
                      ? '' : teacher?.user.email) : "Imported From File",
            emailDisplay: <div className="multiline-break">{getEmailDisplay(teacher?.user.email)}</div>,
            school: teacher ? showSchools(teacher) : null,
            numClasses: teacher ? teacher.info.classes : null,
            numStudents: teacher ? teacher.info.students : null,
            inviteStatus:  
            teacher?.invite  
              ? getInviteStatusDisplay(teacher) 
              : '',
            actions: (
              <div className="row">
                {/* <div className="col-2">
                    <ResendInviteButton
                      isPending={getInviteStatusDisplay(teacher!).replace(' ','_').toLowerCase() == InviteStatuses.INVITE_SENT
                        || getInviteStatusDisplay(teacher!).replace(' ','_').toLowerCase() == InviteStatuses.INVITE_EXPIRED
                      }
                      employee={teacher!}
                    />
                </div> */}
                {settings?.user && (
                  <div className="col-2">
                    <OverlayTrigger
                      overlay={
                        <Tooltip id="deleteIcon" className="customInfoTooltip">
                          View Data Permissions
                        </Tooltip>
                      }
                    >
                      <span onClick={() => handleView(teacher?.user! ,settings)}>
                        <FontAwesomeIcon icon={faEye} />
                      </span>
                    </OverlayTrigger>
                  </div>
                )}
              {/* <div className="col-2">
                <OverlayTrigger
                  overlay={
                    <Tooltip id="deleteIcon" className="customInfoTooltip">
                      Delete
                    </Tooltip>
                  }
                >
                  <span onClick={() => handleDeleteRow(row)}>
                    <FontAwesomeIcon icon={faTrash} />
                  </span>
                </OverlayTrigger>
              </div> */}
              </div>
            ),
          }})}
          columns={columns}
          sort={sortField && sortOrder ? {
            dataField: sortField,
            order: sortOrder,
          } : undefined } 
          // defaultSorted={defaultSorted}
          noDataIndication={isEmpty}
          pagination={sortedMergedData
            .filter((rowData) => { //loggedin user not shown in the list
              let currentEmailAddress = currentUser?.email.replace('@','');
              return !rowData?.user?.email.includes(currentEmailAddress!)
            }).length > 20 ? paginationFactory(options) : undefined}
          onTableChange={handleTableChange}
        />
        {/* <DataSharingEditModal selectedUser={selectedUser} /> */}
        
        <InviteTeachersModal />
      </div>
    }
    </>
  );
};

export default MyTeachersTable;
